import { useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { PanelLeftOpen, X } from "lucide-react";
import { Button } from "../../../../components/tailwind/button";
import { workflowTheme } from "../../utils/workflowBuilderTheme";
import { buildPaletteGroups, getPaletteItemColor } from "../../utils/workflowPaletteGroups";
import { getPaletteIcon } from "../../utils/workflowPaletteIcons";

const DRAG_OPEN_THRESHOLD_PX = 40;
const CLICK_MOVE_TOLERANCE_PX = 6;

/** Left-edge handle shown when the node palette is collapsed. Click or drag right to reopen. */
export function PaletteDrawerHandle({ onOpen }) {
  const startX = useRef(0);
  const opened = useRef(false);

  const handlePointerDown = (event) => {
    startX.current = event.clientX;
    opened.current = false;
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event) => {
    if (opened.current || !event.currentTarget.hasPointerCapture(event.pointerId)) return;
    if (event.clientX - startX.current >= DRAG_OPEN_THRESHOLD_PX) {
      opened.current = true;
      onOpen();
    }
  };

  const handlePointerEnd = (event) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    if (!opened.current && Math.abs(event.clientX - startX.current) <= CLICK_MOVE_TOLERANCE_PX) {
      onOpen();
    }
  };

  return (
    <button
      type="button"
      className={`absolute left-0 top-3 z-30 flex flex-col items-center gap-1.5 rounded-r-lg border border-l-0 ${workflowTheme.border} ${workflowTheme.surface} px-1.5 py-3 shadow-md hover:bg-gray-50 dark:hover:bg-slate-800 cursor-ew-resize touch-none group`}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerCancel={handlePointerEnd}
      aria-label="Open node palette"
      aria-expanded={false}
      title="Open node palette (click or drag right)"
    >
      <PanelLeftOpen
        className="w-4 h-4 text-gray-600 dark:text-gray-300 group-hover:text-[#0F6E56] dark:group-hover:text-emerald-400"
        aria-hidden
      />
      <span
        className="w-0.5 h-7 rounded-full bg-gray-300 dark:bg-slate-600 group-hover:bg-[#0F6E56] dark:group-hover:bg-emerald-500 transition-colors"
        aria-hidden
      />
    </button>
  );
}

const MESSAGE_NODE_TYPES = new Set(["SEND_MESSAGE", "NOTIFY_USER", "AWAIT_USER_REPLY"]);

const mapNodeType = (nodeType) => {
  if (MESSAGE_NODE_TYPES.has(nodeType)) return "message";
  if (nodeType === "CONDITION") return "condition";
  if (nodeType === "ACTION") return "action";
  if (nodeType === "COLLECT_INPUT") return "trigger";
  if (nodeType === "HANDOFF") return "action";
  if (nodeType === "END") return "action";
  if (nodeType === "INVOKE_WORKFLOW") return "action";
  if (nodeType === "TRANSFER_WORKFLOW") return "action";
  return "trigger";
};

export function LeftPalette({ onClose }) {
  const { data: catalogData, loading, error } = useSelector((state) => state.workflowCatalog || {});

  const categories = useMemo(() => {
    return buildPaletteGroups(catalogData).map((group) => ({
      key: group.key,
      title: group.title,
      color: group.color,
      items: group.nodes.map((node) => ({
        id: node.paletteType,
        paletteType: node.paletteType,
        icon: node.icon,
        type: mapNodeType(node.nodeType),
        workflowType: node.nodeType,
        label: node.label,
        description: node.description,
        defaultConfig: node.defaultConfig,
        paletteConfigSchema: node.configSchema,
        color: getPaletteItemColor(node.paletteType) || group.color,
      })),
    }));
  }, [catalogData]);

  return (
    <aside
      className={`w-64 ${workflowTheme.surface} border-r ${workflowTheme.border} flex flex-col h-full overflow-y-auto shrink-0 animate-fade-in`}
      aria-label="Node palette"
    >
      <div className={`relative p-4 pr-10 border-b ${workflowTheme.borderSubtle} ${workflowTheme.surfaceMuted}`}>
        {typeof onClose === "function" && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={`absolute right-1 top-1 h-7 w-7 ${workflowTheme.textMuted} hover:text-gray-700 dark:hover:text-gray-200`}
            onClick={onClose}
            aria-label="Hide node palette"
          >
            <X className="w-4 h-4" />
          </Button>
        )}
        <h3 className={`font-semibold ${workflowTheme.textPrimary} text-sm`}>Node Palette</h3>
        <p className={`text-xs ${workflowTheme.textSecondary} mt-1`}>Drag nodes to canvas</p>
      </div>

      <div className="p-4 space-y-6">
        {loading && <p className={`text-xs ${workflowTheme.textSecondary}`}>Loading catalog...</p>}
        {!loading && error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
        {!loading && !error && categories.length === 0 && (
          <p className={`text-xs ${workflowTheme.textSecondary}`}>No nodes available.</p>
        )}
        {categories.map((category) => (
          <div key={category.key}>
            <h4 className={`text-xs font-bold ${workflowTheme.textMuted} uppercase tracking-wider mb-3`}>
              {category.title}
            </h4>
            <div className="space-y-2">
              {category.items.map((item) => {
                const Icon = getPaletteIcon(item.icon, item.paletteType);
                return (
                  <div
                    key={item.id}
                    className={`flex items-center gap-2 p-2.5 rounded-lg border shadow-sm cursor-grab active:cursor-grabbing hover:shadow-md hover:scale-[1.02] hover:ring-1 hover:ring-[#0F6E56]/25 dark:hover:ring-emerald-500/30 transition-all ${item.color || category.color}`}
                    onDragStart={(event) => {
                      event.dataTransfer.setData("application/reactflow/type", item.type);
                      event.dataTransfer.setData("application/reactflow/label", item.label);
                      event.dataTransfer.setData("application/reactflow/catalog-item", JSON.stringify(item));
                      event.dataTransfer.effectAllowed = "move";
                    }}
                    draggable
                  >
                    <Icon className="w-4 h-4 shrink-0" aria-hidden />
                    <span className="text-xs font-medium leading-tight">{item.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </aside>
  );
}
